import React, { useEffect, useState , useRef } from "react";
import {
  TableWrapper,
  StyledTable,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableCell,
  IconWrapper,
  FactSheetIconWrapper,
  SortIconWrapper,
  CompareAllIconContainer,
  ColumnHeaderWrapper,
  HospitalNameWrapper,
  PartnerLocationIcon,
  RankingCell,
  ColorScaleLegend,
  ColorGradient,
  LegendLabel,
  InfoIconWrapper,
  NoDataFoundText,
  NoDataFoundContainer,
} from "./index.styled";
import { columnsWithSortIcon, TableColumns } from "../../Configs/config";
import {
  formatFloorValue,
  formatNumber,
  getScoreColor,
  getRankColor,
  toTitleCase,
  viewSizeCalculator,
} from "../../utils";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAddBookmark, useRemoveBookmark } from "../../hooks/useBookmarks";
import {
  addHospitalToCompare,
  removeHospitalFromCompare,
} from "./../../Redux/compare/compare.actions";
import { ReactComponent as SelectedCompareHospital } from "../../Assets/Icons/SelectedCompareHospital.svg";
import { ReactComponent as BookMarkUnFilled } from "../../Assets/Icons/BookMarkUnFilled.svg";
import { ReactComponent as Bookmark } from "../../Assets/Icons/Bookmark.svg";
import { ReactComponent as SliderIconGray } from "../../Assets/Icons/SliderIconGray.svg";
import { ReactComponent as CompareAll } from "../../Assets/Icons/CompareAll.svg";
import { ReactComponent as BookmarkAll } from "../../Assets/Icons/BookmarkAll.svg";
import { ReactComponent as RemoveAllFromBookmarks } from "../../Assets/Icons/RemoveAllFromBookmarks.svg";
import { ReactComponent as RemoveAllFromCompare } from "../../Assets/Icons/RemoveAllFromCompare.svg";
import { ReactComponent as PartnerLocation } from "../../Assets/Icons/PartnerLocation.svg";
import { Loader } from "../../App";
import Info from "../Info";
import { useSnack } from "../SnackBar";
import { ReactComponent as SortByIconBlack } from "../../Assets/Icons/SortByIconBlack.svg";
import { ReactComponent as SortByIconGray } from "../../Assets/Icons/SortByIconGray.svg";
import { ReactComponent as InfoIcon } from "../../Assets/Icons/Info.svg";
import { BackgroundOverlay } from "../FilterComp/index.styled";
import ResetModal from "../ResetModal";
import { createPortal } from "react-dom";
import {ReactComponent as NoDataFound} from "../../Assets/Icons/NoDataFound.svg";

const TruncatedText = ({ children, tooltipText, maxWidth }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = textRef.current;

    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [children]);

  return (
    <Info
      disabled={!isTruncated}
      align="start"
      position="center"
      showArrow={false}
      config={{
        tooltipText,
      }}
    >
      <span
        ref={textRef}
        style={{
          display: "inline-block",
          maxWidth,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {children}
      </span>
    </Info>
  );
};
const CustomTable = ({
  data,
  column,
  totalHospitals,
  toggleState,
  setSelectedOption,
  selectedOption,
  sortOrder = "asc",
  setSortOrder,
  selectedIds,
  setSelectedIds,
  isCompareActive,
  view,
  setRemoveIds,
  lastElementRef,
}) => {
  
  const { bookmarkList } = useSelector((state) => state.bookmarks);
  const { compareHospitals } = useSelector((state) => state.compare);
  const [loading, setLoading] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const addBookmark = useAddBookmark();
  const removeBookmark = useRemoveBookmark();
  const snack = useSnack();
  const [columns, setColumns] = useState(column);
  const [bookmarksSelectLoader, setBookmarksSelectLoader] = useState(false);
  const [pendingBookmarkIds, setPendingBookmarkIds] = useState([]);
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (showBookmarkModal) {
      setShouldRender(true);
    }
  }, [showBookmarkModal]);
  
  const handleClose = () => {
    setShouldRender(false);
    setTimeout(() => {
      handleModalCancel();
    }, 300);
  };

  const handleBookmarkSelectAll = async () => {
    const bookmarkedIds = new Set(bookmarkList.map(h => h.provider_number));
    const visibleIds = data.map(row => row.provider_number);
    const areAllVisibleBookmarked = visibleIds.every(id => bookmarkedIds.has(id));
    
    if (areAllVisibleBookmarked) {
      setBookmarksSelectLoader(true);
      const idsToRemove = visibleIds.filter(id => bookmarkedIds.has(id));
      if (idsToRemove.length) {
        await removeBookmark(idsToRemove);
        snack(
          `Removed ${idsToRemove.length} Bookmarks successfully`,
          "#1F1F1F",
          "#FFFFFF"
        );
      }
      setBookmarksSelectLoader(false);
      return;
    }
  
    const nonBookmarkedVisibleIds = visibleIds.filter(id => !bookmarkedIds.has(id));

    setPendingBookmarkIds(nonBookmarkedVisibleIds);

    if(nonBookmarkedVisibleIds.length > 20) {
      setShowBookmarkModal(true);
    }else{
      await processNextBookmarkBatch(nonBookmarkedVisibleIds);
    }
    setBookmarksSelectLoader(false);
  };

  
  const processNextBookmarkBatch = async (pendingIds) => {
    setBookmarksSelectLoader(true);
  
    if (pendingIds.length === 0) {
      setPendingBookmarkIds([]);
      setBookmarksSelectLoader(false);
      return;
    }
  
    const batchSize = Math.min(20, pendingIds.length);
    const currentBatch = pendingIds.slice(0, batchSize);
    await addBookmark(currentBatch);

    if(currentBatch.length >= 20){
      snack(
        "First 20 bookmarked! Tap “Select All” again to pick the next batch.",
        "#1F1F1F",
        "#FFFFFF"
      );
    }else{
      snack(
        `Added ${currentBatch.length} Bookmarks successfully`,
        "#1F1F1F",
        "#FFFFFF"
      );
    }

    setBookmarksSelectLoader(false);
  
    const remainingIds = pendingIds.slice(batchSize);
  
    if (remainingIds.length === 0) {
      setPendingBookmarkIds([]);
    } else {
      setPendingBookmarkIds(remainingIds);
    }
  };

  const handleModalProceed = async () => {
    setShowBookmarkModal(false);
    await processNextBookmarkBatch(pendingBookmarkIds);  
  };
  
  const handleModalCancel = () => {
    setShowBookmarkModal(false);
    setBookmarksSelectLoader(false);
    setPendingBookmarkIds([]);
  };

  const isAllSelected =
    data.length > 0 && selectedIds.length === data.length;

  const isAllBookmarksSelected =
    data.length > 0 &&
    data.every((item) =>
      bookmarkList.some((bookmark) => bookmark.provider_number === item.provider_number)
    );

  useEffect(() => {
    if (compareHospitals?.length && data?.length) {
      const compareIds = new Set(
        compareHospitals.map((item) => item.provider_number),
      );
      const commonIds = data
        .filter((item) => compareIds.has(item.provider_number))
        .map((item) => item.provider_number);
      setSelectedIds(commonIds);
    }
  }, [compareHospitals, data, view]);

  const handleSelectAll = () => {
    const MAX_COMPARE_COUNT = 20;
    const idToRowMap = new Map(data.map(row => [row.provider_number, row]));
  
    if (isAllSelected) {
      const idsToRemove = data
        .map((row) => row.provider_number)
        .filter((id) => compareHospitals.some(h => h.provider_number === id));
  
      if (idsToRemove.length) {
        dispatch(removeHospitalFromCompare(idsToRemove));
      }
  
      setSelectedIds([]);
    } else {
      const newIds = data.map((row) => row.provider_number);
      const currentlyComparedIds = new Set(compareHospitals.map(h => h.provider_number));
      
      const newCompareCount = compareHospitals.length;
      const remainingSpace = MAX_COMPARE_COUNT - newCompareCount;
  
      const idsNotAlreadyCompared = newIds.filter(id => !currentlyComparedIds.has(id));
      const idsToAdd = idsNotAlreadyCompared.slice(0, remainingSpace);
  
      const payloads = idsToAdd.map(id => {
        const data = idToRowMap.get(id);
        if (!data) return null;
        return {
          value: data["hospital name"],
          label: toTitleCase(data["hospital name"]),
          location: data["location"],
          provider_number: data.provider_number,
        };
      }).filter(Boolean);
  
      if (payloads.length) {
        dispatch(addHospitalToCompare(payloads));
      }
  
      const finalSelectedIds = [
        ...newIds.filter(id => currentlyComparedIds.has(id)),
        ...idsToAdd,
      ];
  
      setSelectedIds(finalSelectedIds);
  
      if (idsToAdd.length < idsNotAlreadyCompared.length) {
        snack(
          "Selection capped at 20! The first 20 have been selected.",
          "#FFC640",
          "#333333"
        );
      }else{
        snack(
          "Hospitals added to compare",
          "#1F1F1F",
          "#FFFFFF",
          "View",
          () => {
            if (compareHospitals.length + payloads.length <= 1) {
              snack(
                "Select at least two hospitals to compare!",
                "#FFC640",
                "#333333"
              );
            } else {
              toggleState("isTableVisible");
            }
          }
        );
      }
    }
  };

  useEffect(() => {
    if (!data || data.length === 0) return;

    // Remove the internal intersection observer since we're using LeftSection's lazy loading
    // observerRef.current = new IntersectionObserver((entries) => {
    //   if (entries[0].isIntersecting) {
    //     loadMoreData();
    //   }
    // });

    // if (observerRef.current && document.getElementById("loadMoreTrigger")) {
    //   observerRef.current.observe(document.getElementById("loadMoreTrigger"));
    // }

    // return () => observerRef.current?.disconnect();
  }, [data]);

  // Remove the internal loadMoreData function since we're using LeftSection's lazy loading
  // const loadMoreData = () => {
  //   // This function is no longer needed as data is passed directly
  //   // setVisibleData((prevData) => {
  //   //   const newData = data?.slice(0, prevData.length + 100);
  //   //   setHasMore(newData.length < data.length);
  //   //   return newData;
  //   // });
  // };

  // Remove the useEffect that was setting visibleData
  // useEffect(() => {
  //   // setVisibleData(data?.slice(0, 100));
  //   // setHasMore(data.length > 100);
  // }, [data]);

  // Extract the list of hospital names from bookmarkList
  const bookmarkedHospitalNames =
    bookmarkList?.map((el) => el.name.toLowerCase()) || [];

  // useEffect(() => {
  //   dispatch(resetSelectedState());
  //   dispatch(resetSpecificStateData());
  // }, []);

  const addToBookmark = async (provider_number) => {
    setLoading((prev) => ({ ...prev, [provider_number]: true }));
    const res = await addBookmark(provider_number);

    if (res.success) {
      snack(
        "Bookmark added successfully",
        "#1F1F1F",
        "#FFFFFF"
      );
    } else {
      snack(
        `Failed to Add bookmark: ${res.message}`,
        "#FFC640",
        "#333333"
      );
    }

    setLoading((prev) => ({ ...prev, [provider_number]: false }));
  };

  const removeFromBookmark = async (provider_number) => {
    setLoading((prev) => ({ ...prev, [provider_number]: true }));

    const res = await removeBookmark(provider_number);

    if (res.success) {
      snack(
        "Bookmark removed successfully",
        "#1F1F1F",
        "#FFFFFF"
      );
    } else {
      snack(
        `Failed to remove: ${res.message}`,
        "#FFC640",
        "#333333"
      );
    }

    setLoading((prev) => ({ ...prev, [provider_number]: false }));
  };

  const handleBookmarkToggle = (isBookmarked, providerNumber) => {
    isBookmarked
      ? removeFromBookmark(providerNumber)
      : addToBookmark(providerNumber);
  };

  const isAddedToCompare = (id) => {
    return compareHospitals.some((el) => el.provider_number === id);
  };

  const addToCompare = (data) => {
    const MAX_COMPARE_COUNT = 20;
    const totalCompareCount = compareHospitals.length;

    if (totalCompareCount >= MAX_COMPARE_COUNT) {
      snack(
        "Max Selection reached! Up to 20 Hospitals can be compared",
        "#FFC640",
        "#333333"
      );
      return;
    }

    const payload = {
      value: data["hospital name"],
      label: toTitleCase(data["hospital name"]),
      location: data["location"],
      provider_number: data?.provider_number,
    };

    const updatedCompareCount = totalCompareCount + 1;

    dispatch(addHospitalToCompare(payload));

    snack(
      `View all (${updatedCompareCount}) Hospital added in Compare screen!`,
      "#1F1F1F",
      "#FFFFFF",
      "View",
      () => {
        if (updatedCompareCount<2) {
          snack(
            "Select at least two hospitals to compare!",
            "#FFC640",
            "#333333"
          );
        } else {
          toggleState("isTableVisible");
        }
      }
    );
  };

  const removeFromCompare = (id) => {
    dispatch(removeHospitalFromCompare(id));
    snack("Hospital removed from Compare screen!");
  };

  const handleSort = (sortValue, order, index) => {
    // Update local column state
    const temp = columns;
    temp[index]["order"] = order === "desc" ? "asc" : "desc";
    setColumns(temp);
    
    // Trigger parent component sorting change
    setSelectedOption(sortValue);
    setSortOrder(order);
    
    // Note: The parent component (LeftSection) should handle resetting lazy loading state
    // when selectedOption or sortOrder changes via its useEffect
  };

  if (!data || data.length === 0) {
    return (
      <NoDataFoundContainer>
        <NoDataFound />
        <NoDataFoundText>
          No data available
        </NoDataFoundText>
      </ NoDataFoundContainer>
    );
  }

  // Extract all scores for color grading
  const allScores = data
    .map(row => row.score)
    .filter(score => score && score !== "N/A" && !isNaN(parseFloat(score)));

  return (
    <TableWrapper animate={true}>
      <StyledTable isVisible={isCompareActive}>
        <TableHeader>
          {columns.map((column, index) => (
            <TableHeaderCell key={index}>
              <ColumnHeaderWrapper isFirst={index === 0}>
                <p
                  style={{
                    color: "#333333",
                    opacity: "40%",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem" // small space between text and icon
                  }}
                >
                  {column.header}
                  {column.accessor === TableColumns.RANKING && (
                    <Info
                      showArrow={false}
                      config={{
                        tooltipText: "Color grading based on score: Red (low) to Green (high)",
                        position: "top",
                        offsetY: 10,
                      }}
                      position="start"
                      align="right-end"
                    >
                      <InfoIconWrapper>
                        <InfoIcon />
                      </InfoIconWrapper>
                    </Info>
                  )}
                </p>
                {columnsWithSortIcon.includes(column.header) && (
                  <Info
                    showArrow={false}
                    config={{
                      tooltipText:
                        column.order === "asc"
                          ? "Sort in Ascending Order"
                          : "Sort in Descending Order",
                      position: "top",
                    }}
                    position="start"
                  >
                    <SortIconWrapper
                      onClick={() => {
                        handleSort(column?.sortedBy, column.order, index);
                      }}
                      sortOrder={column.order}
                    >
                      {(() => {
                        const isActive = column?.sortedBy === selectedOption || 
                                       (!selectedOption && column?.sortedBy === "state");
                        return isActive ? <SortByIconBlack /> : <SortByIconGray />;
                      })()}
                    </SortIconWrapper>
                  </Info>
                )}
              </ColumnHeaderWrapper>
              {column.accessor === "slider" && (
                <Info
                  showArrow={false}
                  config={{
                    tooltipText: !isAllSelected ? "Add All to Compare" : "Remove All from Compare",
                    position: "top",
                    offsetY: 10,
                  }}
                  align = {isAllSelected ? "right-end" : "center"}
                >
                  <CompareAllIconContainer onClick={handleSelectAll}>
                    {!isAllSelected ? <CompareAll /> : <RemoveAllFromCompare />}
                  </CompareAllIconContainer>
                </Info>
              )}
              {column.accessor === "bookmark" && (
                <Info
                  showArrow={false}
                  config={{
                    tooltipText: !isAllBookmarksSelected ? "Add All to Bookmarks" : "Remove All from Bookmarks",
                    position: "top",
                    offsetY: 10,
                  }}
                  position = "start"
                  align="right-end"
                >
                  <IconWrapper onClick={bookmarksSelectLoader ? null : handleBookmarkSelectAll}>
                    {bookmarksSelectLoader ? (
                      <Loader
                        height={viewSizeCalculator(1.3)}
                        width={viewSizeCalculator(1.3)}
                      />
                    ) : (
                      !isAllBookmarksSelected ? <BookmarkAll /> : <RemoveAllFromBookmarks />
                    )}
                  </IconWrapper>
                </Info>
              )}
            </TableHeaderCell>
          ))}
        </TableHeader>
        <tbody>
          {data.map((row, rowIndex) => {
            const isBookmarked = bookmarkedHospitalNames.includes(
              row["hospital name"]?.toLowerCase(),
            );
            const isLastRow = rowIndex === data.length - 1;
            return (
              <TableRow 
                key={rowIndex}
                ref={isLastRow && lastElementRef ? lastElementRef : null}
              >
                {columns.map((column, colIndex) => {
                  let cellValue = row[column.accessor];
                  if (
                    column.accessor === "net patient revenue" &&
                    cellValue !== 0 &&
                    cellValue !== "N/A"
                  )
                    cellValue = formatNumber(`${cellValue}`);
                  
                  // Apply color grading for ranking column
                  const isRankingColumn = column.accessor === TableColumns.RANKING;
                  // const scoreColor = isRankingColumn ? getScoreColor(row.score, allScores) : {};
                  const scoreColor = isRankingColumn ? getRankColor(row.ranking, totalHospitals): {};
                  
                  // Removed formatFloorValue for patient discharges since it's already formatted in formatHospitalDetails
                  return (
                    <TableCell
                      onClick={() =>
                        column.header === TableColumns.HOSPITAL_NAME &&
                        navigate(`/hospitals/${row.provider_number}`)
                      }
                      isCursorPointer={
                        column.accessor === TableColumns.SLIDER ||
                        column.accessor === TableColumns.SHARE ||
                        column.accessor === TableColumns.BOOKMARK ||
                        column.header === TableColumns.HOSPITAL_NAME ||
                        column.header === TableColumns.ACCREDITATIONS ||
                        column.accessor === TableColumns.RANKING
                      }
                      isRightAligned={
                        column.accessor === TableColumns.NO_OF_BEDS ||
                        column.accessor === TableColumns.GROSS_REVENUE ||
                        column.accessor === TableColumns.PATIENT_DISCHARGES ||
                        column.accessor === TableColumns.CMS_RATINGS ||
                        column.accessor === TableColumns.RANKING
                      }
                      key={colIndex}
                      isHospitalName={
                        column.header === TableColumns.HOSPITAL_NAME
                      }
                      style={scoreColor}
                      isFirst={rowIndex === 0}
                    >
                      {column.accessor === TableColumns.SLIDER ? (
                        <>
                          <Info
                            showArrow={false}
                            config={{
                              tooltipText: isAddedToCompare(row.provider_number)
                                ? "Remove from Compare"
                                : "Add to Compare",
                              position: "top",
                              offsetY: 10,
                            }}
                          >
                            {!isAddedToCompare(row.provider_number) && (
                              <IconWrapper>
                                <SliderIconGray
                                  onClick={() => addToCompare(row)}
                                />
                              </IconWrapper>
                            )}
                          </Info>
                          <Info
                            showArrow={false}
                            config={{
                              tooltipText: isAddedToCompare(row.provider_number)
                                ? "Remove from Compare"
                                : "Add to Compare",
                              position: "top",
                              offsetY: 10,
                            }}
                          >
                            {isAddedToCompare(row.provider_number) && (
                              <IconWrapper>
                                <SelectedCompareHospital
                                  onClick={() =>
                                    removeFromCompare(row.provider_number)
                                  }
                                />
                              </IconWrapper>
                            )}
                          </Info>
                        </>
                      ) : column.accessor === TableColumns.SHARE ? (
                        <Info
                          showArrow={false}
                          config={{
                            tooltipText: "Open Factsheet",
                            position: "top",
                            offsetY: 10,
                          }}
                        >
                          <FactSheetIconWrapper>
                            {/* <ShareIcon
                              onClick={() => handleShareClick(row.provider_number)}
                            /> */}
                          </FactSheetIconWrapper>
                        </Info>
                      ) : column.accessor === TableColumns.BOOKMARK ? (
                        <>
                          {loading[row.provider_number] ? (
                            <Loader
                              height={viewSizeCalculator(1.3)}
                              width={viewSizeCalculator(1.3)}
                            />
                          ) : (
                            <div
                              onClick={() =>
                                handleBookmarkToggle(
                                  isBookmarked,
                                  row.provider_number,
                                )
                              }
                            >
                              <Info
                                showArrow={false}
                                config={{
                                  tooltipText: "Bookmark",
                                  position: "top",
                                  offsetY: 10,
                                }}
                                position="start"
                              >
                                {isBookmarked ? (
                                  <IconWrapper>
                                    <Bookmark
                                      style={{
                                        fill: "#F6B51C",
                                        color: "#F6B51C",
                                      }}
                                    />
                                  </IconWrapper>
                                ) : (
                                  <IconWrapper>
                                    <BookMarkUnFilled
                                      style={{ fill: "none", color: "#333333" }}
                                    />
                                  </IconWrapper>
                                )}
                              </Info>
                            </div>
                          )}
                        </>
                        ) : column.header === TableColumns.HOSPITAL_NAME ? (
                          <HospitalNameWrapper>
                            {row.esa === 1 && (
                              <PartnerLocationIcon>
                                <PartnerLocation />
                              </PartnerLocationIcon>
                            )}
                            <TruncatedText
                              tooltipText={cellValue}
                              // Icon (1) + gap (0.4) must stay inside the original
                              // 14rem name budget so other columns don't squeeze.
                              maxWidth={viewSizeCalculator(
                                row.esa === 1 ? 12.6 : 14,
                              )}
                            >
                              {toTitleCase(cellValue)}
                            </TruncatedText>
                          </HospitalNameWrapper>

                        ) : column.header === TableColumns.ACCREDITATIONS ||
                            column.accessor === TableColumns.OWNERSHIP_TYPE ? (

                          <TruncatedText
                            tooltipText={cellValue}
                            maxWidth={viewSizeCalculator(10)}
                          >
                            {cellValue}
                          </TruncatedText>

                        ) : isRankingColumn ? (
                        <Info
                          showArrow={false}
                          config={{
                            tooltipText: `Score: ${
                              row.score != null && !isNaN(Number(row.score))
                                ? (Number(row.score) * 100).toFixed(2)
                                : "N/A"
                            }`,
                            position: "top",
                            offsetY: 10,
                          }}
                        >
                          <RankingCell style={scoreColor}>
                            {cellValue}
                          </RankingCell>
                        </Info>
                      ) : (
                        cellValue
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </tbody>
      </StyledTable>
      
      {/* Color Scale Legend for Ranking */}
      {/* {data.some(row => row.score && row.score !== "N/A") && (
        <ColorScaleLegend>
          <div style={{ display: "flex", alignItems: "center", gap: `${viewSizeCalculator(0.5)}` }}>
            <span>Score Color Scale:</span>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: `${viewSizeCalculator(0.25)}`,
              flex: 1
            }}>
              <LegendLabel color="#c34141">Low</LegendLabel>
              <ColorGradient />
              <LegendLabel color="#4e9e6a">High</LegendLabel>
            </div>
          </div>
        </ColorScaleLegend>
      )} */}
      
      {(showBookmarkModal || shouldRender) &&
        createPortal(
          <>
            <BackgroundOverlay isPopupVisible={shouldRender} />
            <ResetModal
              title="Select up to 20 hospitals at a time!"
              description={`You can bookmark 20 hospitals at once.\nTo bookmark more, please select and save them in batches`}
              onProceed={handleModalProceed}
              onCancel={handleClose}
              showResetModal={shouldRender}
              isSelectModal={true}
            />
          </>,
          document.body
        )}
      {/* hasMore && (
        <LoadMoreContainer id="loadMoreTrigger">
          <TableLoader />
        </LoadMoreContainer>
      ) */}
    </TableWrapper>
  );
};

export default CustomTable;
